import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import type { User, Organization, ApiResponse, AuthResponse, LoginInput, RegisterInput } from '@/types';

interface AuthState {
  user: User | null;
  organization: Organization | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  googleAuth: (googleId: string, email: string, name: string, avatarUrl?: string) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [state, setState] = useState<AuthState>({
    user: null,
    organization: null,
    isLoading: true,
    isAuthenticated: false,
  });

  const handleAuthResponse = useCallback((data: AuthResponse) => {
    api.setTokens(data.accessToken, data.refreshToken);
    setState({
      user: data.user,
      organization: data.organization,
      isLoading: false,
      isAuthenticated: true,
    });
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    const res: ApiResponse<AuthResponse> = await api.post('/auth/login', input);
    if (!res.success || !res.data) {
      throw new Error(res.error ?? 'Login failed');
    }
    handleAuthResponse(res.data);
  }, [handleAuthResponse]);

  const register = useCallback(async (input: RegisterInput) => {
    const res: ApiResponse<AuthResponse> = await api.post('/auth/register', input);
    if (!res.success || !res.data) {
      throw new Error(res.error ?? 'Registration failed');
    }
    handleAuthResponse(res.data);
  }, [handleAuthResponse]);

  const googleAuth = useCallback(async (
    googleId: string,
    email: string,
    name: string,
    avatarUrl?: string,
  ) => {
    const res: ApiResponse<AuthResponse> = await api.post('/auth/google', {
      googleId,
      email,
      name,
      avatarUrl,
    });
    if (!res.success || !res.data) {
      throw new Error(res.error ?? 'Google authentication failed');
    }
    handleAuthResponse(res.data);
  }, [handleAuthResponse]);

  const logout = useCallback(() => {
    api.clearTokens();
    setState({
      user: null,
      organization: null,
      isLoading: false,
      isAuthenticated: false,
    });
  }, []);

  const refreshAuth = useCallback(async () => {
    const token = api.getAccessToken();
    if (!token) {
      setState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      const res: ApiResponse<{ user: User; organization: Organization }> = await api.get('/auth/me');
      if (res.success && res.data) {
        setState({
          user: res.data.user,
          organization: res.data.organization,
          isLoading: false,
          isAuthenticated: true,
        });
      } else {
        api.clearTokens();
        setState({
          user: null,
          organization: null,
          isLoading: false,
          isAuthenticated: false,
        });
      }
    } catch {
      api.clearTokens();
      setState({
        user: null,
        organization: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  const value: AuthContextValue = {
    ...state,
    login,
    register,
    googleAuth,
    logout,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
